import {
  BeakerIcon,
  ClipboardDocumentCheckIcon,
  CubeTransparentIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { PageHeading } from "@via/components/page-heading.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import ActivityCard from "@via/pages/activity/activity-card.tsx";
import { useRecentActivity } from "@via/pages/activity/activity.api.tsx";
import { PAGE_NAMES, ROUTES } from "@via/utils/constants.tsx";
import { type JSX, useCallback, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import ActivityRunList from "@via/pages/activity/activity-run-list";
import ActivitySampleExplorer from "@via/pages/activity/activity-sample-explorer";
import "./activity.css";
import { classNames } from "@via/utils/helpers.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { ActivityRunResponse, Tab } from "@via/utils/interfaces";

type ActivityTab = "Runs" | "Samples";

function Activity({
  hideContentWrapper,
}: {
  hideContentWrapper?: boolean;
}): JSX.Element {
  const { data: recentActivityData } = useRecentActivity();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState<ActivityTab>("Runs");
  const [samplesTabVisited, setSamplesTabVisited] = useState(false);
  const [runListData, setRunListData] = useState<
    | {
        data: ActivityRunResponse[];
        total: number;
      }
    | undefined
  >();

  const routePattern = useMemo(() => {
    if (pathname.includes("/organization/") && pathname.endsWith("/activity")) {
      return ROUTES.ORGANIZATION_ACTIVITY;
    }
    return ROUTES.ACTIVITY;
  }, [pathname]);

  const handleTabChange = useCallback((tab: ActivityTab) => {
    setActiveTab(tab);
    if (tab === "Samples") {
      setSamplesTabVisited(true);
    }
  }, []);

  const handleRunListDataChange = useCallback(
    (data: { data: ActivityRunResponse[]; total: number } | undefined) => {
      setRunListData(data);
    },
    []
  );

  // Register Cortex context with combined data
  const getContext = useCallback(() => {
    return {
      recentActivity: {
        projects: recentActivityData?.projects?.slice(0, 5) || [],
        pipelines: recentActivityData?.pipelines?.slice(0, 5) || [],
      },
      runList: {
        runs: runListData?.data || [],
        total: runListData?.total || 0,
      },
    };
  }, [recentActivityData, runListData]);

  useRegisterCortexContext({
    routePattern,
    contextName: "Activity Page",
    getContext,
    systemPrompt:
      "You are analyzing the activity page. Help answer questions about recent projects, pipelines, and runs.",
    enabled: !!recentActivityData,
  });

  const tabOptions: Tab<ActivityTab>[] = useMemo(
    () => [
      {
        name: "Runs",
        customTitle: (
          <span className="flex items-center gap-1.5 font-semibold">
            <RocketLaunchIcon className="h-5 w-5" />
            Runs
          </span>
        ),
        children: (
          <div className="mt-4">
            <ActivityRunList onDataChange={handleRunListDataChange} />
          </div>
        ),
      },
      {
        name: "Samples",
        customTitle: (
          <span className="flex items-center gap-1.5 font-semibold">
            <ClipboardDocumentCheckIcon className="h-5 w-5" />
            Samples
          </span>
        ),
        children: samplesTabVisited ? (
          <div className="mt-4">
            <ActivitySampleExplorer />
          </div>
        ) : undefined,
      },
    ],
    [samplesTabVisited, handleRunListDataChange]
  );

  return (
    <main
      className={classNames(hideContentWrapper ? "" : "via-content-wrapper")}>
      <PageHeading title={PAGE_NAMES.ACTIVITY} />
      {recentActivityData && (
        <div className="mt-6">
          <h2 id="activity-projects-title" className="via-h2">
            Projects
          </h2>
          <div className="mb-8 mt-2 flex flex-row">
            {recentActivityData?.projects.slice(0, 5).map((item, i) => (
              <ActivityCard
                key={`${i}-${item.id}`}
                item={item}
                icon={<BeakerIcon className="h-6 w-6" />}
                href={`/projects/${item.id}`}
              />
            ))}
            <Link to={ROUTES.PROJECTS} className="view-all-link">
              View All
            </Link>
          </div>
          <h2 className="via-h2">Pipelines</h2>
          <div className="mb-8 mt-2 flex flex-row">
            {recentActivityData?.pipelines.slice(0, 5).map((item, i) => (
              <ActivityCard
                key={`${i}-${item.id}`}
                item={item}
                icon={<CubeTransparentIcon className="h-6 w-6" />}
                href={`/pipelines/${item.pipelineId}`}
              />
            ))}
            <Link to={"/pipelines"} className="view-all-link">
              View All
            </Link>
          </div>
          <HorizontalTabs<ActivityTab>
            options={tabOptions}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            buttonIdPrefix="activity"
            variant="underline"
            fullWidth
          />
        </div>
      )}
    </main>
  );
}

export default Activity;
